/**
 * Google Cloud Storage credential modal (Adjust → GCS integration).
 *
 * Opened from the "Google Cloud" tile in Settings → Integrations.
 * GCS creds are per-app (Adjust delivers one bucket per app), so the
 * modal carries an app picker. Flow: pick app → enter project/bucket →
 * upload service-account JSON → Save → Test connection. A successful
 * test enables ingestion (the 5-min worker job picks the app up).
 *
 * Reuses the per-app /api/app-settings/gcs-credentials endpoints.
 */

import { useEffect, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import { Btn } from "@/components/Btn";
import {
  useApps,
  useAppGcsCredentials,
  useTestGcsConnection,
  useUpsertAppGcsCredentials,
} from "@/lib/api-hooks";

export function GcsConfigModal({ onClose: onCloseProp }: { onClose: () => void }) {
  const apps = useApps();
  const [appId, setAppId] = useState<number | null>(null);

  useEffect(() => {
    if (appId === null && apps.data && apps.data.length > 0) {
      setAppId(apps.data[0].id);
    }
  }, [apps.data, appId]);

  const credsQuery = useAppGcsCredentials(appId);
  const upsert = useUpsertAppGcsCredentials();
  const test = useTestGcsConnection();

  const [projectId, setProjectId] = useState("");
  const [bucket, setBucket] = useState("");
  const [saJson, setSaJson] = useState<Record<string, unknown> | null>(null);
  const [saFileName, setSaFileName] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const creds = credsQuery.data ?? null;

  useEffect(() => {
    setProjectId(creds?.gcs_project_id ?? "");
    setBucket(creds?.gcs_bucket_name ?? "");
    setSaJson(null);
    setSaFileName(null);
    setMsg(null);
  }, [creds?.app_id, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const guard = useDiscardGuard(true, projectId !== (creds?.gcs_project_id ?? "") || bucket !== (creds?.gcs_bucket_name ?? "") || saJson != null, onCloseProp);
  const onClose = guard.requestClose;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const text = await file.text();
      const obj = JSON.parse(text) as Record<string, unknown>;
      if (!obj.client_email || !obj.private_key) {
        throw new Error("not a service-account key (missing client_email/private_key)");
      }
      setSaJson(obj);
      setSaFileName(file.name);
      setMsg(null);
    } catch (e) {
      setSaJson(null);
      setSaFileName(null);
      setMsg({ ok: false, text: `Invalid JSON: ${(e as Error).message}` });
    }
  };

  const canSave =
    appId != null &&
    projectId.trim().length > 0 &&
    bucket.trim().length > 0 &&
    (saJson != null || creds?.has_service_account === true);

  const onSave = async () => {
    if (appId == null) return;
    try {
      await upsert.mutateAsync({
        app_id: appId,
        gcs_project_id: projectId.trim(),
        gcs_bucket_name: bucket.trim(),
        service_account_json: saJson ?? undefined,
      });
      setSaJson(null);
      setSaFileName(null);
      setMsg({ ok: true, text: "Saved. Now click “Test connection” to enable." });
    } catch (e) {
      setMsg({ ok: false, text: `Save failed: ${(e as Error).message}` });
    }
  };

  const onTest = async () => {
    if (appId == null) return;
    try {
      const r = await test.mutateAsync({ app_id: appId });
      setMsg(
        r.ok
          ? {
              ok: true,
              text: `Connection OK — bucket reachable (${r.object_count ?? 0}+ objects). Ingestion enabled.`,
            }
          : { ok: false, text: `Test failed: ${r.detail ?? "unknown error"}` },
      );
    } catch (e) {
      setMsg({ ok: false, text: `Test failed: ${(e as Error).message}` });
    }
  };

  const connected = creds?.enabled === true;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(14,18,32,0.45)",
        display: "grid",
        placeItems: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 96vw)",
          background: "var(--c-surface)",
          borderRadius: 12,
          boxShadow: "var(--sh-3)",
          padding: 24,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>
          Connect Google Cloud Storage
        </h3>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 0, marginBottom: 18 }}>
          Adjust delivers cohort-event CSVs to a GCS bucket (one per app).
          Save the service-account key, then test the connection to enable
          ingestion (runs every 5 minutes).
        </p>

        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label">App</label>
          <select
            className="select"
            value={appId ?? ""}
            onChange={(e) => setAppId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— pick app —</option>
            {(apps.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name ?? a.bundle_id}
              </option>
            ))}
          </select>
        </div>

        {appId != null && (
          <>
            <div
              style={{
                marginBottom: 14,
                fontSize: 13,
                fontWeight: 600,
                color: connected ? "#137333" : "var(--c-text-3)",
              }}
            >
              Status: {connected ? "Connected ✓" : "Not connected"}
              {creds?.last_test_status && (
                <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                  (last test: {creds.last_test_status})
                </span>
              )}
            </div>

            <div className="field" style={{ marginBottom: 14 }}>
              <label className="label">Project ID</label>
              <input
                className="input"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="my-gcp-project"
              />
            </div>
            <div className="field" style={{ marginBottom: 14 }}>
              <label className="label">Bucket name</label>
              <input
                className="input"
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
                placeholder="adjust-exports-grids"
              />
            </div>
            <div className="field" style={{ marginBottom: 14 }}>
              <label className="label">Service Account JSON</label>
              <input
                type="file"
                accept="application/json,.json"
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                {saFileName
                  ? `Selected: ${saFileName}`
                  : creds?.has_service_account
                    ? "A key is already stored — upload a new file only to replace it."
                    : "Upload the service-account key file."}
              </div>
            </div>

            {msg && (
              <div
                style={{
                  marginBottom: 12,
                  fontSize: 12.5,
                  color: msg.ok ? "#137333" : "#c5221f",
                }}
              >
                {msg.text}
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn onClick={onClose}>Close</Btn>
          <Btn
            disabled={!creds?.has_service_account || test.isPending}
            onClick={() => void onTest()}
          >
            {test.isPending ? "Testing…" : "Test connection"}
          </Btn>
          <Btn variant="primary" disabled={!canSave || upsert.isPending} onClick={() => void onSave()}>
            {upsert.isPending ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
